import { defineStore } from 'pinia';
import api from '@/common/utils/api';
import { useAuthStore } from '@/common/store/pinia/auth';
import type {
  S3_service,
  S3_related_user_key_with_secret,
  S3_related_user_key,
  S3_related_user_info,
  ProjectS3TaskInfo
} from '@schema';
import waitTask from '@/common/utils/waitTask';
import ServerError from '@/common/utils/errors/ServerError';
import NotFoundError from '@/common/utils/errors/NotFoundError';

const API_URL = '/api/v2/s3service';

const handleError = (error, state, $reset) => {
  if (
    error instanceof ServerError
    && error.errorCode
  ) {
    state.isS3Available = !['s3_disabled', 's3_not_found'].includes(error.errorCode);
    state.isS3UserEnabled = error.errorCode !== 's3_user_disabled';
    if (!state.isS3Available || !state.isS3UserEnabled) {
      $reset();
    }
  }

  throw error;
};

type State = {
  config: S3_service | null,
  user: Required<S3_related_user_info>
  isS3Available: boolean | null,
  isS3Deployed: boolean,
  isS3UserEnabled: boolean,
  domainsS3Status: Record<ID, boolean>,
};

export default defineStore('s3Service', {
  state: (): State => ({
    config: null,
    user: {
      arn: '',
      enabled: true,
      keys: [],
    },
    isS3Available: null,
    isS3Deployed: false,
    isS3UserEnabled: false,
    domainsS3Status: {},
  }),
  getters: {
    getDomainS3Status: (state: State) => (domainId: ID) => state.domainsS3Status[domainId] || false,
    isLegacyS3User: (state: State) => !!state.config?.s3_user_id,
    scopedProjectId(): string | undefined {
      return this.isLegacyS3User ? undefined : useAuthStore().currentUserProjectId;
    },
  },
  actions: {
    async getS3Service() {
      try {
        const { data } = await api.get<S3_service>(API_URL);
        this.isS3Available = true;
        this.$patch({ config: data });
        return data;
      } catch (error) {
        return handleError(error, this.$state, this.$reset.bind(this));
      }
    },
    async createS3Service() {
      try {
        const { data } = await api.post<S3_service>(API_URL);
        this.config = data;
        return data;
      } catch (error) {
        return handleError(error, this.$state, this.$reset.bind(this));
      }
    },
    async getUser() {
      try {
        const { data } = await api.get<S3_related_user_info>(`${API_URL}/user`);
        this.$patch({ user: data });
        return data;
      } catch (error) {
        return handleError(error, this.$state, this.$reset.bind(this));
      }
    },
    async createKeys(payload) {
      try {
        const { data } = await api.post<S3_related_user_key_with_secret>(`${API_URL}/keys`, payload);
        const { sec_key, ...key } = data;
        this.user.keys.push(key);
        return data;
      } catch (error) {
        return handleError(error, this.$state, this.$reset.bind(this));
      }
    },
    async updateKey(accesskey, payload) {
      try {
        const { data } = await api.put<S3_related_user_key>(`${API_URL}/keys/${accesskey}`, payload);
        const newKey = data[0];
        this.user.keys = this.user.keys.map((key) => (key.acc_key === newKey.acc_key ? newKey : key));
        return data;
      } catch (error) {
        return handleError(error, this.$state, this.$reset.bind(this));
      }
    },
    async deleteKey(accesskey) {
      try {
        await api.delete(`${API_URL}/keys/${accesskey}`);
        this.user.keys = this.user.keys.filter((key) => key.acc_key !== accesskey);
        return true;
      } catch (error) {
        return handleError(error, this.$state, this.$reset.bind(this));
      }
    },
    async getS3ServiceDomainStatus(domainId: ID) {
      const { data } = await api.get(`${API_URL}/domain/${domainId}`);
      this.isS3Deployed = true;
      this.domainsS3Status = {
        ...this.domainsS3Status,
        [domainId]: data
      };
      return data;
    },
    async checkS3Deployed(domainId: ID) {
      try {
        await this.getS3ServiceDomainStatus(domainId);
      } catch {
        this.isS3Deployed = false;
      }
    },
    async setS3ServiceDomainStatus(domainId: ID, enabled) {
      const { data } = await api.put(`${API_URL}/domain/${domainId}`, { enabled });
      await waitTask(data.task_id);
      this.domainsS3Status[domainId] = enabled;
      return data;
    },
    async getProjectS3Status(domainId: ID, projectId: ID) {
      const { data } = await api.get(`/api/v2/domains/${domainId}/projects/${projectId}/s3`);
      return data;
    },
    async enableProjectS3(domainId: ID, projectId: ID) {
      const { data } = await api.post(`/api/v2/domains/${domainId}/projects/${projectId}/s3`);
      return data;
    },
    async disableProjectS3(domainId: ID, projectId: ID) {
      const { data } = await api.patch(`/api/v2/domains/${domainId}/projects/${projectId}/s3/`, {
        enabled: false
      });
      return data;
    },
    async setProjectS3Quotas(domainId: ID, projectId: ID, quotaGigabytes: number) {
      const { data } = await api.post(`/api/v2/domains/${domainId}/projects/${projectId}/s3/quotas`, {
        quota_gigabytes: quotaGigabytes
      });
      return data;
    },
    deleteProjectS3Quotas(domainId: ID, projectId: ID) {
      return api.delete(`/api/v2/domains/${domainId}/projects/${projectId}/s3/quotas`);
    },
    async getDomainS3Quotas(domainId: ID) {
      try {
        const { data } = await api.get(`/api/v2/domains/${domainId}/s3/quotas`);
        return data;
      } catch (error) {
        if (error instanceof NotFoundError) {
          return null;
        }
        throw error;
      }
    },
    async setDomainS3Quotas(domainId: ID, quotaGigabytes: number) {
      const { data } = await api.post(`/api/v2/domains/${domainId}/s3/quotas`, {
        quota_gigabytes: quotaGigabytes
      });
      return data;
    },
    deleteDomainS3Quotas(domainId: ID) {
      return api.delete(`/api/v2/domains/${domainId}/s3/quotas`);
    },
    async getDomainS3UsageSummary(domainId: ID) {
      try {
        const { data } = await api.get(`/api/v2/domains/${domainId}/s3/usage/summary/`);
        return data;
      } catch (error) {
        if (error instanceof NotFoundError) {
          return null;
        }
        throw error;
      }
    },
    async migrateUserToProject(domainId: ID, domainUserId: ID, projectId: ID) {
      const { data } = await api.post<ProjectS3TaskInfo>(
        `/api/v2/domains/${domainId}/s3/migration/user/${domainUserId}/to-project/${projectId}/`
      );
      await waitTask(data.task_id);
      return data;
    },
    async getProjectS3UsageSummary(domainId: ID, projectId: ID) {
      try {
        const { data } = await api.get(`/api/v2/domains/${domainId}/projects/${projectId}/s3/usage`);
        return data?.data?.[0]?.SpaceStat ?? null;
      } catch (error) {
        if (error instanceof NotFoundError) {
          return null;
        }
        throw error;
      }
    }
  },
});
